import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2 } from 'lucide-react';
import { Text, Badge, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { Table } from '@components/base/data';
import { HStack, VStack } from '@components/base/layout';
import { useAdminListPage } from '../../hooks/useAdminListPage';
import { AdminListPage } from '../../components/AdminListPage';
import { workflowDefinitionsService } from '../../api/workflowDefinitionsService';
import type { WorkflowDefinition } from '../../api/workflowDefinitionsService';

export function WorkflowDefinitionsListPage() {
  const navigate = useNavigate();

  const listPage = useAdminListPage<WorkflowDefinition, string>({
    queryKey: ['admin', 'workflows'],
    queryFn: () => workflowDefinitionsService.list(),
    deleteFn: (slug) => workflowDefinitionsService.remove(slug),
    getDeleteId: (definition) => definition.slug,
  });

  return (
    <AdminListPage<WorkflowDefinition>
      title="Workflow Definitions"
      subtitle="Create and manage workflow definitions"
      loadingLabel="Loading workflow definitions..."
      errorTitle="Failed to load workflow definitions"
      errorFallback="An unexpected error occurred while loading workflow definitions."
      emptyTitle="No workflow definitions found"
      emptyDescription="Get started by creating your first workflow definition."
      createLabel="Create Workflow"
      onCreateClick={() => navigate('/admin/workflows/create')}
      items={listPage.items}
      getRowKey={(definition) => definition.id}
      onRowClick={(definition) => navigate(`/admin/workflows/${definition.slug}`)}
      isLoading={listPage.isLoading}
      error={listPage.error}
      deleteModalTitle="Delete Workflow"
      deleteTarget={listPage.deleteTarget}
      getDeleteTargetName={(definition) => definition.name}
      deleteNoun="workflow definition"
      onDeleteCancel={listPage.cancelDelete}
      onDeleteConfirm={listPage.confirmDelete}
      isDeleting={listPage.isDeleting}
      renderHead={() => (
        <>
          <Table.HeaderCell>Name</Table.HeaderCell>
          <Table.HeaderCell align="center">Version</Table.HeaderCell>
          <Table.HeaderCell align="center">Status</Table.HeaderCell>
          <Table.HeaderCell align="center">Steps</Table.HeaderCell>
          <Table.HeaderCell align="center">Executions</Table.HeaderCell>
          <Table.HeaderCell align="right">Actions</Table.HeaderCell>
        </>
      )}
      renderRow={(definition) => (
        <>
          <Table.Cell>
            <VStack gap={0}>
              <Text weight="medium">{definition.name}</Text>
              <Text size="sm" color="muted">
                {definition.slug}
              </Text>
            </VStack>
          </Table.Cell>
          <Table.Cell align="center">
            <Badge variant="info" size="sm">
              v{definition.version}
            </Badge>
          </Table.Cell>
          <Table.Cell align="center">
            <Badge
              variant={definition.is_active ? 'success' : 'default'}
              size="sm"
            >
              {definition.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </Table.Cell>
          <Table.Cell align="center">
            <Text size="sm">{definition.steps_count ?? 0}</Text>
          </Table.Cell>
          <Table.Cell align="center">
            <Text size="sm">{definition.executions_count ?? 0}</Text>
          </Table.Cell>
          <Table.Cell align="right">
            <HStack gap={1} justify="end">
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${definition.name}`}
                leftIcon={<Icon icon={Pencil} size="sm" />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/workflows/${definition.slug}/edit`);
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete ${definition.name}`}
                leftIcon={<Icon icon={Trash2} size="sm" color="error" />}
                onClick={(e) => {
                  e.stopPropagation();
                  listPage.setDeleteTarget(definition);
                }}
              />
            </HStack>
          </Table.Cell>
        </>
      )}
    />
  );
}
